import type { Block, Cell, ChipCell, Column, Tone } from '@/kova/spec/types'

import type { ApiClient, RequestOptions } from '@/lib/api'
import type { ScreenData, ScreenLoader } from './provenance'
import {
  asArray, asRecord, attempt, disclosure, knows, malformed, moneyCell, moneyReadable,
  readBool, readInstant, readInt, readText, receipt, refLine, summarise,
  type Attempt,
} from './wire'

/**
 * The authorisation decision log, and the two queues over it (#167).
 *
 *   rk-decisions  GET /v1/admin/decisions
 *   rk-review     GET /v1/admin/decisions?outcome=review&open=true
 *   rk-stepup     GET /v1/admin/decisions?outcome=step_up&open=true
 *
 * Three screens, one endpoint, one vocabulary. The vocabulary lives here once
 * because three copies of "which outcomes exist" is three chances for a fifth
 * to appear — which is the single thing law 14 forbids.
 *
 * The per-ref explorer is `./decision.ts` and stays there: it hangs off a
 * REFUSAL rather than a screen, because any screen can be refused and the
 * reference an operator is handed has to resolve the same way wherever they
 * were refused from.
 *
 * # Law 14 — the set is closed and there is no `freeze`
 *
 * `allow | review | step_up | refuse_to_submit`, from the engine's own
 * declaration: "The set is closed." An outcome outside it renders with the
 * value the backend sent and an attention tone, and is recorded as a gap. It is
 * never mapped into one of the four — that would hide a vocabulary change — and
 * never given a chip of its own, which would be the fifth the law refuses.
 *
 * And the half that is easy to miss: there is no `freeze` to reach for, because
 * only the institution may impose that state — law 1. Nothing here can
 * produce one.
 *
 * # Three drawn columns this list cannot answer, each refused by name
 *
 *   - THE SNAPSHOTS. Not on any list row, and it is a MONEY-PATH constraint
 *     rather than a response-size preference: migration 046 records those JSONB
 *     columns being dragged through shared buffers by an admin poll and
 *     evicting the pages the cash-out path needs, degrading a customer read
 *     bounded at 300 ms p95. They are on the per-ref read, one row at a time.
 *   - WHAT TRIPPED. `primary_reason` is a coarse code the engine writes and
 *     never a typology name. The name is inside a JSONB and the VERSION exists
 *     only inside the snapshot no list row may read, so returning the name
 *     alone "would let a console draw a 'what tripped' column whose one
 *     quotable part is the part missing". Dropped rather than half-filled.
 *   - A BY-OUTCOME DEPTH. Counting this table grouped by outcome is a
 *     sequential scan of an append-only log carrying two JSONB columns per row
 *     — the exact scan migration 046 was raised to remove from it.
 *
 * # Two fields that decide whether a decision governed anything
 *
 * `shadow` is a decision that was recorded and governed nothing. Counted into a
 * review queue it would put work in front of somebody that was never owed, so
 * it is said in words rather than left to a flag nobody reads.
 *
 * `current`, with `supersedes` and `superseded_by`, is the other: a superseded
 * decision is not the one that governed, and a queue showing it as live is a
 * queue somebody works twice.
 *
 * # Read-only, and the database agrees
 *
 * No approve, clear, reassign or state change. A reviewer's action is a NEW
 * decision through the authorisation seam, never an edit of this row
 * (invariants 8 and 9) — and `authz_decisions` refuses UPDATE and DELETE by
 * trigger, so a control added here "would fail at the database rather than
 * merely be wrong".
 */

export const DECISIONS_PATH = '/decisions'

const SOURCE = 'postgres · authz_decisions (GET /v1/admin/decisions)'
/** Ours, and the record of what we decided — not what the institution decided. */
const AUTHORITY = 'operational — our own authorisation record'

/**
 * The closed set, and the tones.
 *
 * `refuse_to_submit` is critical because it is the one outcome a customer felt.
 * `review` and `step_up` are attention: they are WORK, and a queue that is red
 * in every row teaches an operator that red means nothing. `allow` is neutral
 * rather than green — an allowed decision is not an achievement, it is the
 * ordinary case, and green on it would drown the rows that need reading.
 */
const OUTCOME_TONE: Readonly<Record<string, Tone>> = {
  allow: 'n',
  review: 'a',
  step_up: 'a',
  refuse_to_submit: 'r',
}

/** Exported so a test can assert the rendered vocabulary is exactly these four. */
export const OUTCOMES: readonly string[] = Object.keys(OUTCOME_TONE)

const COLS: readonly Column[] = [
  ['decision_ref', 'm', 'minmax(180px,1.3fr)'],
  ['At', 'm', '150px'],
  ['Customer', 'm', '104px'],
  ['Flow', 'x', '150px'],
  ['Amount', 'a', '146px'],
  ['Outcome', 'c', '150px'],
  ['Primary reason', 'm', '180px'],
  ['Governed', 'c', '190px'],
  ['Engine', 'x', '120px'],
]

interface RowRead {
  readonly cells: readonly Cell[]
  readonly gaps: readonly string[]
  readonly ref: string | null
  readonly shadow: boolean
  readonly superseded: boolean
  readonly unknownOutcome: boolean
}

/**
 * Whether this decision governed anything, which is two facts and not one.
 *
 * A shadow decision was recorded and bound nothing. A superseded one bound
 * something once and no longer does. Both render as not-governing, and both say
 * WHICH — the remedies differ, and a single "inactive" chip would lose that.
 */
function governedCell(o: Record<string, unknown>, gaps: string[]): {
  readonly cell: ChipCell; readonly shadow: boolean; readonly superseded: boolean
} {
  const shadow = readBool(o, 'shadow')
  if (shadow === null) gaps.push('whether a decision was a shadow run')
  const current = readBool(o, 'current')
  const supersededBy = readText(o, 'superseded_by')

  if (shadow === true) {
    return { cell: ['shadow · governed nothing', 'n'], shadow: true, superseded: false }
  }
  if (current === false || supersededBy !== null) {
    return {
      cell: [supersededBy === null ? 'superseded' : `superseded by ${supersededBy}`, 'n'],
      shadow: false, superseded: true,
    }
  }
  if (current === null) {
    gaps.push('whether a decision is the current one')
    return { cell: ['unstated', 'a'], shadow: false, superseded: false }
  }
  return { cell: ['governed this attempt', 'g'], shadow: false, superseded: false }
}

function readDecisionRow(raw: unknown): RowRead | null {
  const o = asRecord(raw)
  if (o === null) return null
  const gaps: string[] = []

  const ref = readText(o, 'decision_ref')
  if (ref === null) gaps.push('a decision reference')

  const at = readInstant(o, 'at')
  if (at.kind !== 'at') gaps.push('when a decision was made')

  // Absent on a decision about no particular customer — a position, not a gap.
  const customer = readInt(o, 'customer_id')

  const flow = readText(o, 'flow')
  if (flow === null) gaps.push('a flow')

  // `omitempty`: a decision on a flow with no amount carries none, and a zero
  // would be a figure where there is not one.
  const hasAmount = asRecord(o['amount']) !== null
  if (hasAmount && !moneyReadable(o, 'amount')) gaps.push('an amount')

  const outcome = readText(o, 'outcome')
  if (outcome === null) gaps.push('an outcome')
  const known = outcome !== null && knows(OUTCOME_TONE, outcome)
  // Never mapped into one of the four. A vocabulary change must be visible,
  // and a fifth chip is what law 14 refuses — so it renders as sent, marked.
  if (outcome !== null && !known) gaps.push('an outcome outside the closed set of four')
  const outcomeCell: ChipCell = outcome === null
    ? ['unstated', 'a']
    : [outcome, known ? (OUTCOME_TONE[outcome] ?? 'a') : 'a']

  const reason = readText(o, 'primary_reason')
  if (reason === null) gaps.push('a primary reason')

  const governed = governedCell(o, gaps)

  return {
    cells: [
      ref ?? '— none',
      at.kind === 'at' ? at.label : '—',
      customer.kind === 'exact' ? String(customer.value) : '— no customer',
      flow ?? '—',
      hasAmount ? moneyCell(o, 'amount') : '— no amount on this flow',
      outcomeCell,
      reason ?? '— none',
      governed.cell,
      readText(o, 'engine') ?? '—',
    ],
    gaps,
    ref,
    shadow: governed.shadow,
    superseded: governed.superseded,
    unknownOutcome: outcome !== null && !known,
  }
}

/** Named in every screen's provenance — the three the design draws and this read cannot answer. */
const MISSING: readonly string[] = [
  'the policy and input snapshots — no list row carries them, and that is a money-path constraint rather than a response-size choice; they are on the per-decision read, one row at a time',
  'what tripped — the typology name is inside a JSONB and its version exists only inside the snapshot no list row may read, so the name is withheld rather than shown without the part anybody would quote',
  'a by-outcome queue depth — counting this log grouped by outcome is the sequential scan the schema was changed to remove from this table',
]

interface Variant {
  readonly kicker: string
  readonly title: string
  readonly emptyBody: string
  readonly heading: string
  readonly query: Readonly<Record<string, string>>
  readonly scope: string | null
}

export function composeDecisions(read: Attempt, v: Variant, at: Date): ScreenData {
  if (!read.ok) {
    return {
      blocks: [{
        t: 'note', tone: 'r', kicker: v.kicker, title: `${v.title} could not be read`,
        body: `${read.reason} ${v.emptyBody}`,
        ref: refLine(read, 'Nothing on this screen was read.'),
      }],
      provenance: { kind: 'unread', source: SOURCE, reason: read.reason },
    }
  }

  const o = asRecord(read.payload)
  const list = o === null ? null : asArray(o['decisions'])
  if (o === null || list === null) {
    const bad = malformed('The decision log returned a shape this console does not know.')
    return {
      blocks: [{
        t: 'note', tone: 'r', kicker: v.kicker, title: `${v.title} could not be read`,
        body: bad.reason, ref: 'Nothing on this screen was read.',
      }],
      provenance: { kind: 'unread', source: SOURCE, reason: bad.reason },
    }
  }

  const gaps: string[] = []
  const rows: (readonly Cell[])[] = []
  const rowKeys: (string | null)[] = []
  let unreadable = 0
  let shadow = 0
  let superseded = 0
  let unknown = 0

  for (const raw of list) {
    const row = readDecisionRow(raw)
    if (row === null) { unreadable += 1; continue }
    rows.push(row.cells)
    rowKeys.push(row.ref)
    gaps.push(...row.gaps)
    if (row.shadow) shadow += 1
    if (row.superseded) superseded += 1
    if (row.unknownOutcome) unknown += 1
  }

  const hasMore = readBool(o, 'has_more') === true

  const footer = rows.length === 0
    ? (unreadable === 0
        ? `${v.emptyBody} The log was read and holds nothing matching this view.`
        : `No decision could be read: ${String(unreadable)} came back in a shape this console does not know.`)
    : `${String(rows.length)} ${rows.length === 1 ? 'decision' : 'decisions'}${v.scope === null ? '' : ` · ${v.scope}`}${hasMore ? ' · the backend caps the page' : ''}${unreadable === 0 ? '' : ` · ${String(unreadable)} row(s) could not be read`}`

  const blocks: Block[] = [{
    t: 'table', title: v.heading, src: 'ledger', facets: false, cols: COLS, rows, footer,
  }]

  // A shadow row in a queue is work nobody was ever owed.
  if (shadow > 0) {
    blocks.push({
      t: 'note', tone: 'n', kicker: 'Shadow',
      title: `${String(shadow)} of these governed nothing`,
      body: 'A shadow decision was recorded for comparison and bound no attempt. Nothing was refused, stepped up or held because of it.',
      ref: 'shadow = true',
    })
  }

  if (superseded > 0) {
    blocks.push({
      t: 'note', tone: 'n', kicker: 'Superseded',
      title: `${String(superseded)} ${superseded === 1 ? 'is' : 'are'} no longer the decision that governs`,
      body: 'A later decision replaced them. They are kept because the log is append-only, and reading one as live is how the same attempt gets worked twice.',
      ref: 'current = false',
    })
  }

  // Law 14: an outcome outside the closed set is a finding, not a new chip.
  if (unknown > 0) {
    blocks.push({
      t: 'note', tone: 'a', kicker: 'Outcome',
      title: `${String(unknown)} decision${unknown === 1 ? '' : 's'} carried an outcome this console does not know`,
      body: 'The risk outcomes are a closed set of four. A value outside it is shown exactly as the backend sent it rather than mapped into one of them, because a vocabulary change must be visible.',
      ref: 'allow · review · step_up · refuse_to_submit',
    })
  }

  const snapshot = disclosure(o, 'snapshot_note', 'Evidence', 'Why the snapshots are not here', 'n')
  if (snapshot !== null && rows.length > 0) blocks.push(snapshot)
  const tripped = disclosure(o, 'what_tripped_note', 'What tripped', 'Why there is no typology column', 'n')
  if (tripped !== null && rows.length > 0) blocks.push(tripped)
  const depth = disclosure(o, 'depth_note', 'Depth', 'Why there is no count by outcome', 'n')
  if (depth !== null && rows.length > 0) blocks.push(depth)

  const missing = [...MISSING, ...summarise(gaps)]
  if (unreadable > 0) missing.push(`${String(unreadable)} decision row(s) this console could not read`)

  return {
    blocks,
    provenance: { kind: 'partial', source: SOURCE, authority: AUTHORITY, readAt: receipt(at), missing },
    title: v.title,
    subject: `authz_decisions · ${String(rows.length)} ${rows.length === 1 ? 'row' : 'rows'}`,
    rowKeys,
  }
}

/**
 * The three variants.
 *
 * `open=true` on the two queues, so they show what still awaits attention
 * rather than every decision that ever took that outcome — a review queue
 * listing every review ever held is a queue nobody can work.
 */
export const LOG_VARIANT: Variant = {
  kicker: 'Decisions', title: 'Authorisation decisions',
  emptyBody: 'No authorisation decision has been recorded in this view.',
  heading: 'Recent authorisation decisions', query: {}, scope: null,
}

export const REVIEW_VARIANT: Variant = {
  kicker: 'Review queue', title: 'Review queue',
  emptyBody: 'Nothing is awaiting review.',
  heading: 'Decisions awaiting review',
  query: { outcome: 'review', open: 'true' },
  scope: 'a filtered view of the one decision log, not a separate table',
}

export const STEP_UP_VARIANT: Variant = {
  kicker: 'Step-up queue', title: 'Step-up queue',
  emptyBody: 'Nothing is awaiting a step-up.',
  heading: 'Decisions awaiting a step-up',
  query: { outcome: 'step_up', open: 'true' },
  scope: 'a filtered view of the one decision log, not a separate table',
}

function load(v: Variant): ScreenLoader {
  return async (api: ApiClient) => {
    const options: RequestOptions = { query: v.query }
    return composeDecisions(
      await attempt(() => api.get<unknown>(DECISIONS_PATH, options), `The ${v.kicker.toLowerCase()}`),
      v, new Date(),
    )
  }
}

export const loadDecisionLog = load(LOG_VARIANT)
export const loadReviewQueueDecisions = load(REVIEW_VARIANT)
export const loadStepUpQueue = load(STEP_UP_VARIANT)
